import {useState} from "react";
import {Button, Card, Col, DatePicker, Form, message, Row, Statistic, Table} from "antd";
import {PrinterOutlined, SearchOutlined} from "@ant-design/icons";
import Api from "../../services/Api";
import dayjs from "dayjs";
import trLocale from "dayjs/locale/tr";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import flexCurrency from "../../utils/FlexCurrency";

dayjs.locale(trLocale);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const {RangePicker} = DatePicker;

const printStyle = `
  .fk-aralik-table .ant-table-cell { padding: 3px !important; }
  @media print {
    body * { visibility: hidden; }
    #fk-print-area, #fk-print-area * { visibility: visible; }
    #fk-print-area { position: absolute; top: 0; left: 0; width: 100%; }
    .no-print { display: none !important; }
  }
`;

const GISE_LABELS = {1: "1. Gişe", 2: "2. Gişe"};
const GISE_COLORS = {1: "blue", 2: "purple"};
const GISE_HEX   = {1: "#1677ff", 2: "#722ed1"};

const columns = [
    {
        title: "Tarih",
        dataIndex: "date",
        render: d => new Date(d).toLocaleDateString("tr-TR"),
    },
    {
        title: "Çekim",
        dataIndex: "shots",
        className: "text-align-right",
        render: v => v.toLocaleString("tr-TR"),
    },
    {
        title: "Satış",
        dataIndex: "sales",
        className: "text-align-right",
        render: v => v.toLocaleString("tr-TR"),
    },
    {
        title: <span style={{color: GISE_HEX[1]}}>1. Gişe Tutar (₺)</span>,
        dataIndex: "amount1",
        className: "text-align-right",
        render: v => <b style={{color: GISE_HEX[1]}}>{flexCurrency(v)}</b>,
    },
    {
        title: <span style={{color: GISE_HEX[2]}}>2. Gişe Tutar (₺)</span>,
        dataIndex: "amount2",
        className: "text-align-right",
        render: v => <b style={{color: GISE_HEX[2]}}>{flexCurrency(v)}</b>,
    },
    {
        title: "Toplam Tutar (₺)",
        className: "text-align-right",
        render: r => <b style={{color: "#52c41a"}}>{flexCurrency(r.amount1 + r.amount2)}</b>,
    },
];

const FotoKapanTarihAralikliRaporPage = () => {
    const [data, setData]       = useState([]);
    const [ozet, setOzet]       = useState(null);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    const getData = (values) => {
        const start = dayjs(values.tarihAraligi[0]).startOf("day");
        const end   = dayjs(values.tarihAraligi[1]).endOf("day");

        setLoading(true);
        setOzet(null);
        setData([]);

        Api.get("/api/FotoKapan/GetTotalKazanclar")
            .then(res => {
                const filtrelenmis = res.data
                    .filter(item => {
                        const d = dayjs(item.date);
                        return d.isSameOrAfter(start) && d.isSameOrBefore(end);
                    })
                    .sort((a, b) =>
                        new Date(b.date) - new Date(a.date) || a.station_id - b.station_id
                    );

                // Her tarih için tek satır: shots/sales bir kez, her gişenin tutarı ayrı sütunda
                const uniqueDates = [...new Set(filtrelenmis.map(i => i.date))];
                const tableRows = uniqueDates.map(date => {
                    const s1 = filtrelenmis.find(i => i.date === date && i.station_id === 1);
                    const s2 = filtrelenmis.find(i => i.date === date && i.station_id === 2);
                    return {
                        date,
                        shots:   (s1 ?? s2)?.shots ?? 0,
                        sales:   (s1 ?? s2)?.sales ?? 0,
                        amount1: s1?.amount ?? 0,
                        amount2: s2?.amount ?? 0,
                    };
                });
                setData(tableRows);

                // Gişe bazlı toplamlar
                const stationIds = [...new Set(filtrelenmis.map(i => i.station_id))].sort();
                const stationTotals = {};
                stationIds.forEach(sid => {
                    const st = filtrelenmis.filter(i => i.station_id === sid);
                    stationTotals[sid] = {
                        shots:  st.reduce((s, i) => s + i.shots,  0),
                        sales:  st.reduce((s, i) => s + i.sales,  0),
                        amount: st.reduce((s, i) => s + i.amount, 0),
                    };
                });

                // API aynı tarih için her gişeye aynı shots/sales değerini gönderiyor.
                // shots ve sales toplama için tarihe göre tekilleştir; amount tüm satırların toplamıdır.
                const uniqueDateItems = Object.values(
                    filtrelenmis.reduce((acc, i) => {
                        if (!acc[i.date]) acc[i.date] = i;
                        return acc;
                    }, {})
                );
                const genelToplam = {
                    shots:  uniqueDateItems.reduce((s, i) => s + i.shots,  0),
                    sales:  uniqueDateItems.reduce((s, i) => s + i.sales,  0),
                    amount: filtrelenmis.reduce((s, i) => s + i.amount, 0),
                };

                setOzet({
                    startDate: start.format("DD.MM.YYYY"),
                    endDate:   end.format("DD.MM.YYYY"),
                    stationIds,
                    stationTotals,
                    genelToplam,
                });

                if (filtrelenmis.length === 0) {
                    message.warning("Seçilen tarih aralığında veri bulunamadı.");
                } else {
                    message.success("Rapor verileri başarıyla alındı.");
                }
            })
            .catch(() => message.error("Veriler alınırken hata oluştu."))
            .finally(() => setLoading(false));
    };

    return (
        <div className={"p-1"}>
            <style>{printStyle}</style>

            {/* Filtre */}
            <Card title={"Foto Kapan Tarih Aralıklı Rapor"} className={"no-print"}>
                <Form
                    form={form}
                    onFinish={getData}
                    layout={"inline"}
                    initialValues={{tarihAraligi: [dayjs().startOf("month"), dayjs()]}}
                >
                    <Form.Item
                        label={"Tarih Aralığı"}
                        name={"tarihAraligi"}
                        rules={[{required: true, message: "Lütfen tarih aralığı seçiniz."}]}
                    >
                        <RangePicker
                            format={"DD.MM.YYYY"}
                            allowClear={false}
                            presets={[
                                {label: "Bu Hafta",  value: [dayjs().startOf("week"), dayjs()]},
                                {label: "Bu Ay",     value: [dayjs().startOf("month"), dayjs()]},
                                {label: "Geçen Ay",  value: [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")]},
                                {label: "Son 3 Ay",  value: [dayjs().subtract(3, "month"), dayjs()]},
                                {label: "Bu Yıl",    value: [dayjs().startOf("year"), dayjs()]},
                            ]}
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button type={"primary"} htmlType={"submit"} icon={<SearchOutlined/>} loading={loading}>
                            Raporu Getir
                        </Button>
                    </Form.Item>

                    {ozet && (
                        <Form.Item>
                            <Button icon={<PrinterOutlined/>} onClick={() => window.print()}>
                                Yazdır
                            </Button>
                        </Form.Item>
                    )}
                </Form>
            </Card>

            <div id={"fk-print-area"}>
                {/* Özet */}
                {ozet && (
                    <Card
                        className={"mt-1"}
                        size={"small"}
                        title={`Foto Kapan Özet — ${ozet.startDate} / ${ozet.endDate}`}
                    >
                        <Row gutter={[16, 16]}>
                            {/* 1. Gişe Tutar */}
                            <Col xs={24} sm={6}>
                                <Card size={"small"} style={{borderTop: `3px solid ${GISE_HEX[1]}`}}>
                                    <div style={{fontWeight: 700, marginBottom: 8, color: GISE_HEX[1]}}>1. Gişe</div>
                                    <Statistic
                                        title={"Tutar"}
                                        value={ozet.stationTotals[1]?.amount ?? 0}
                                        suffix={"₺"}
                                        valueStyle={{fontSize: 14, color: GISE_HEX[1]}}
                                        formatter={v => flexCurrency(v)}
                                    />
                                </Card>
                            </Col>

                            {/* 2. Gişe Tutar */}
                            <Col xs={24} sm={6}>
                                <Card size={"small"} style={{borderTop: `3px solid ${GISE_HEX[2]}`}}>
                                    <div style={{fontWeight: 700, marginBottom: 8, color: GISE_HEX[2]}}>2. Gişe</div>
                                    <Statistic
                                        title={"Tutar"}
                                        value={ozet.stationTotals[2]?.amount ?? 0}
                                        suffix={"₺"}
                                        valueStyle={{fontSize: 14, color: GISE_HEX[2]}}
                                        formatter={v => flexCurrency(v)}
                                    />
                                </Card>
                            </Col>

                            {/* Toplam Çekim & Satış */}
                            <Col xs={24} sm={6}>
                                <Card size={"small"} style={{borderTop: "3px solid #faad14"}}>
                                    <div style={{fontWeight: 700, marginBottom: 8, color: "#faad14"}}>Toplam</div>
                                    <Row gutter={8}>
                                        <Col span={12}>
                                            <Statistic
                                                title={"Çekim"}
                                                value={ozet.genelToplam.shots}
                                                valueStyle={{fontSize: 14}}
                                                formatter={v => v.toLocaleString("tr-TR")}
                                            />
                                        </Col>
                                        <Col span={12}>
                                            <Statistic
                                                title={"Satış"}
                                                value={ozet.genelToplam.sales}
                                                valueStyle={{fontSize: 14}}
                                                formatter={v => v.toLocaleString("tr-TR")}
                                            />
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>

                            {/* Genel Toplam */}
                            <Col xs={24} sm={6}>
                                <Card size={"small"} style={{borderTop: "3px solid #52c41a"}}>
                                    <div style={{fontWeight: 700, marginBottom: 8, color: "#52c41a"}}>Genel Toplam</div>
                                    <Statistic
                                        title={"Tutar"}
                                        value={ozet.genelToplam.amount}
                                        suffix={"₺"}
                                        valueStyle={{fontSize: 14, color: "#52c41a", fontWeight: "bold"}}
                                        formatter={v => flexCurrency(v)}
                                    />
                                </Card>
                            </Col>
                        </Row>
                    </Card>
                )}

                {/* Detay tablosu */}
                {(data.length > 0 || loading) && (
                    <Card bodyStyle={{padding: 0}} className={"mt-1"} loading={loading}>
                        <Table
                            className={"fk-aralik-table"}
                            size={"small"}
                            bordered
                            dataSource={data}
                            rowKey={r => r.date}
                            columns={columns}
                            pagination={false}
                        />
                    </Card>
                )}
            </div>
        </div>
    );
};

export default FotoKapanTarihAralikliRaporPage;
